/**
 * Buchungsjournal und Umsatzsteuer-Auswertung.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { formatAmount, formatDate, today } from '@erp/shared';

import { api, withQuery } from '../api/client';
import { PageHeader } from '../components/Layout';
import { Alert, EmptyState, LoadingBlock, TextInput } from '../components/ui';

type JournalLine = {
  id: string;
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
  taxCode: string | null;
  taxAmount: number;
  contraAccount: string | null;
  description: string | null;
  reference: string | null;
};

type JournalEntry = {
  id: string;
  entryNumber: string;
  entryDate: string;
  source: string;
  documentNumber: string | null;
  description: string;
  debitTotal: number;
  creditTotal: number;
  exportedAt: string | null;
  lines: JournalLine[];
};

const SOURCE_LABELS: Record<string, string> = {
  sales: 'Ausgangsrechnung',
  purchase: 'Eingangsrechnung',
  payment: 'Zahlung',
  manual: 'Manuell',
  opening: 'Eröffnung',
};

function startOfYear(): string {
  return `${new Date().getFullYear()}-01-01`;
}

export function JournalPage() {
  const [tab, setTab] = useState<'journal' | 'vat'>('journal');
  const [from, setFrom] = useState(startOfYear());
  const [to, setTo] = useState(today());

  return (
    <>
      <PageHeader title="Buchhaltung" />

      <div className="row" style={{ marginBottom: 16, gap: 6 }}>
        <button
          type="button"
          className={`btn btn-sm${tab === 'journal' ? ' btn-primary' : ''}`}
          onClick={() => setTab('journal')}
        >
          Buchungsjournal
        </button>
        <button
          type="button"
          className={`btn btn-sm${tab === 'vat' ? ' btn-primary' : ''}`}
          onClick={() => setTab('vat')}
        >
          Umsatzsteuer
        </button>
      </div>

      <div className="card">
        <div className="table-toolbar">
          <div className="row" style={{ gap: 10 }}>
            <label className="row" style={{ gap: 6, fontSize: 13 }}>
              von
              <TextInput type="date" value={from} onChange={setFrom} />
            </label>
            <label className="row" style={{ gap: 6, fontSize: 13 }}>
              bis
              <TextInput type="date" value={to} onChange={setTo} />
            </label>
          </div>
        </div>
      </div>

      {tab === 'journal' ? <Journal from={from} to={to} /> : <VatReport from={from} to={to} />}
    </>
  );
}

function Journal({ from, to }: { from: string; to: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['journal', from, to],
    queryFn: () =>
      api.get<{ entries: JournalEntry[]; total: number }>(
        withQuery('/api/accounting/journal', { from, to, limit: 200 }),
      ),
  });

  if (isLoading) return <LoadingBlock />;
  if (!data) return null;

  if (data.entries.length === 0) {
    return (
      <div className="card">
        <EmptyState
          title="Keine Buchungen in diesem Zeitraum"
          hint="Buchungen entstehen automatisch beim Festschreiben von Rechnungen und beim Erfassen von Zahlungen."
        />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>{data.total} Buchungssätze</h2>
      </div>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th className="shrink">Nummer</th>
              <th className="shrink">Datum</th>
              <th className="shrink">Herkunft</th>
              <th>Buchungstext</th>
              <th className="shrink">Konto</th>
              <th className="numeric">Soll</th>
              <th className="numeric">Haben</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((entry) =>
              entry.lines.map((line, index) => (
                <tr key={line.id}>
                  {index === 0 ? (
                    <>
                      <td className="mono" rowSpan={entry.lines.length}>
                        {entry.entryNumber}
                        {entry.exportedAt ? (
                          <div className="faint" style={{ fontSize: 11 }}>
                            exportiert
                          </div>
                        ) : null}
                      </td>
                      <td rowSpan={entry.lines.length} style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(entry.entryDate)}
                      </td>
                      <td rowSpan={entry.lines.length} className="faint">
                        {SOURCE_LABELS[entry.source] ?? entry.source}
                      </td>
                      <td rowSpan={entry.lines.length}>
                        {entry.description}
                        {entry.documentNumber ? (
                          <div className="faint mono" style={{ fontSize: 12 }}>
                            {entry.documentNumber}
                          </div>
                        ) : null}
                      </td>
                    </>
                  ) : null}
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {line.accountNumber}
                    <span className="faint"> {line.accountName.slice(0, 28)}</span>
                  </td>
                  <td className="numeric">{line.debit > 0 ? formatAmount(line.debit) : ''}</td>
                  <td className="numeric">{line.credit > 0 ? formatAmount(line.credit) : ''}</td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VatReport({ from, to }: { from: string; to: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['vat', from, to],
    queryFn: () =>
      api.get<{
        revenueNet: number;
        vatOutput: number;
        vatInput: number;
        payable: number;
        details: {
          accountNumber: string;
          accountName: string;
          net: number;
          tax: number;
          kind: string;
        }[];
      }>(withQuery('/api/accounting/vat', { from, to })),
  });

  if (isLoading) return <LoadingBlock />;
  if (!data) return null;

  return (
    <>
      <Alert kind="info">
        Diese Auswertung ist eine Hilfe zur Vorbereitung der
        Umsatzsteuer-Voranmeldung, keine Anmeldung selbst. Sie ersetzt nicht die Prüfung durch
        Ihren Steuerberater.
      </Alert>

      <div className="tile-grid">
        <div className="tile">
          <div className="tile-label">Umsätze netto</div>
          <div className="tile-value">{formatAmount(data.revenueNet)} €</div>
        </div>
        <div className="tile">
          <div className="tile-label">Umsatzsteuer</div>
          <div className="tile-value">{formatAmount(data.vatOutput)} €</div>
        </div>
        <div className="tile">
          <div className="tile-label">Vorsteuer</div>
          <div className="tile-value">{formatAmount(data.vatInput)} €</div>
        </div>
        <div className="tile">
          <div className="tile-label">
            {data.payable >= 0 ? 'Zahllast an das Finanzamt' : 'Erstattung'}
          </div>
          <div
            className="tile-value"
            style={{ color: data.payable >= 0 ? 'var(--danger)' : 'var(--success)' }}
          >
            {formatAmount(Math.abs(data.payable))} €
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <h2>Konten im Zeitraum</h2>
        </div>
        {data.details.length === 0 ? (
          <EmptyState title="Keine steuerrelevanten Buchungen in diesem Zeitraum" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Konto</th>
                  <th>Bezeichnung</th>
                  <th className="shrink">Art</th>
                  <th className="numeric">Bemessungsgrundlage</th>
                  <th className="numeric">Steuer</th>
                </tr>
              </thead>
              <tbody>
                {data.details.map((detail) => (
                  <tr key={detail.accountNumber}>
                    <td className="mono">{detail.accountNumber}</td>
                    <td>{detail.accountName}</td>
                    <td className="faint">
                      {detail.kind === 'revenue'
                        ? 'Erlöse'
                        : detail.kind === 'vatOutput'
                          ? 'Umsatzsteuer'
                          : 'Vorsteuer'}
                    </td>
                    <td className="numeric">{detail.net !== 0 ? formatAmount(detail.net) : ''}</td>
                    <td className="numeric">{detail.tax !== 0 ? formatAmount(detail.tax) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
